import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type FileInvite, type FileRef, api, apiUploadFileProxy } from "../lib/api.ts";
import { socketService } from "../lib/socket.ts";

interface Props {
  syncKey: string;
}

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export default function Files({ syncKey }: Props) {
  const [files, setFiles] = useState<FileRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [openActionsFor, setOpenActionsFor] = useState<string | null>(null);
  const [actionSection, setActionSection] = useState<Record<string, "share" | "invite" | "delete" | null>>({});
  const [inviteUsernames, setInviteUsernames] = useState<Record<string, string>>({});
  const [inviteBusy, setInviteBusy] = useState<Record<string, boolean>>({});
  const [shareCopyBusy, setShareCopyBusy] = useState<Record<string, boolean>>({});
  const [shareOpenBusy, setShareOpenBusy] = useState<Record<string, boolean>>({});
  const [revokeBusy, setRevokeBusy] = useState<Record<string, boolean>>({});
  const [inviteStatus, setInviteStatus] = useState<Record<string, string>>({});
  const [incomingInvites, setIncomingInvites] = useState<FileInvite[]>([]);
  const [outgoingInvites, setOutgoingInvites] = useState<FileInvite[]>([]);
  const [acceptedDownload, setAcceptedDownload] = useState<{ url: string; senderEmail: string | null } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.files
      .list(syncKey)
      .then((r) => {
        setFiles(r.files);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [syncKey]);

  const loadInvites = useCallback(() => {
    Promise.all([api.files.incomingInvites(syncKey), api.files.outgoingInvites(syncKey)])
      .then(([incoming, outgoing]) => {
        setIncomingInvites(incoming.invites);
        setOutgoingInvites(outgoing.invites);
      })
      .catch(() => {});
  }, [syncKey]);

  useEffect(() => {
    load();
    loadInvites();
    return socketService.on("file:ready", () => {
      load();
      loadInvites();
    });
  }, [load, loadInvites]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".file-actions-row")) return;
      setOpenActionsFor(null);
    }

    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { fileId } = await apiUploadFileProxy(syncKey, file);
      console.log("Uploaded", fileId);
      load();
      // Upload visibility can lag briefly; re-pull until the new file appears.
      for (let i = 0; i < 5; i += 1) {
        const listed = await api.files.list(syncKey).catch(() => null);
        if (listed?.files.some((f) => f.id === fileId)) {
          setFiles(listed.files);
          break;
        }
        await delay(500);
      }
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function copyShareLink(id: string) {
    setShareCopyBusy((prev) => ({ ...prev, [id]: true }));
    setInviteStatus((prev) => ({ ...prev, [id]: "" }));
    try {
      const { url } = await api.files.shareUrl(syncKey, id);
      await navigator.clipboard.writeText(url);
      setInviteStatus((prev) => ({ ...prev, [id]: "Copied 1-hour share link" }));
    } catch {
      setInviteStatus((prev) => ({ ...prev, [id]: "Failed to copy share link" }));
    } finally {
      setShareCopyBusy((prev) => ({ ...prev, [id]: false }));
    }
  }

  async function downloadFile(file: FileRef) {
    setShareOpenBusy((prev) => ({ ...prev, [file.id]: true }));
    setInviteStatus((prev) => ({ ...prev, [file.id]: "" }));
    try {
      const { url } = await api.files.shareUrl(syncKey, file.id);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setInviteStatus((prev) => ({ ...prev, [file.id]: "Download started" }));
    } catch {
      setInviteStatus((prev) => ({ ...prev, [file.id]: "Failed to download file" }));
    } finally {
      setShareOpenBusy((prev) => ({ ...prev, [file.id]: false }));
    }
  }

  async function del(id: string) {
    await api.files.delete(syncKey, id);
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  async function invite(fileId: string) {
    const recipientUsername = (inviteUsernames[fileId] ?? "").trim().toLowerCase();
    if (!recipientUsername) return;

    setInviteBusy((prev) => ({ ...prev, [fileId]: true }));
    setInviteStatus((prev) => ({ ...prev, [fileId]: "" }));
    try {
      await api.files.invite(syncKey, fileId, recipientUsername);
      setInviteStatus((prev) => ({ ...prev, [fileId]: `Invitation sent to @${recipientUsername}` }));
      setInviteUsernames((prev) => ({ ...prev, [fileId]: "" }));
      loadInvites();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message === "recipient_not_found"
            ? "Username not found"
            : err.message
          : "Failed to send invite";
      setInviteStatus((prev) => ({ ...prev, [fileId]: msg }));
    } finally {
      setInviteBusy((prev) => ({ ...prev, [fileId]: false }));
    }
  }

  async function revokeInvite(inviteId: string, fileId: string) {
    setRevokeBusy((prev) => ({ ...prev, [inviteId]: true }));
    setInviteStatus((prev) => ({ ...prev, [fileId]: "" }));
    try {
      await api.files.revokeInvite(syncKey, inviteId);
      setInviteStatus((prev) => ({ ...prev, [fileId]: "Invitation removed" }));
      loadInvites();
    } catch {
      setInviteStatus((prev) => ({ ...prev, [fileId]: "Failed to remove invitation" }));
    } finally {
      setRevokeBusy((prev) => ({ ...prev, [inviteId]: false }));
    }
  }

  function outgoingForFile(fileId: string) {
    return outgoingInvites.filter((invite) => invite.file?.id === fileId);
  }

  function toggleActions(fileId: string) {
    setOpenActionsFor((prev) => (prev === fileId ? null : fileId));
    setActionSection((prev) => ({
      ...prev,
      [fileId]: prev[fileId] ?? "share",
    }));
  }

  function setSection(fileId: string, section: "share" | "invite" | "delete") {
    setActionSection((prev) => ({ ...prev, [fileId]: section }));
  }

  async function acceptInvite(inviteId: string) {
    try {
      const res = await api.files.acceptInvite(syncKey, inviteId);
      setAcceptedDownload({
        url: res.download.url,
        senderEmail: res.contact.sender.email,
      });
      loadInvites();
    } catch {
      // ignore
    }
  }

  async function declineInvite(inviteId: string) {
    try {
      await api.files.declineInvite(syncKey, inviteId);
      loadInvites();
    } catch {
      // ignore
    }
  }

  return (
    <div className="page">
      <div className="settings-section">
        <h2>Incoming Invitations</h2>
        {incomingInvites.length === 0 ? (
          <p className="empty">No incoming invites.</p>
        ) : (
          <div className="invite-list">
            {incomingInvites.map((invite) => (
              <div className="invite-card" key={invite.id}>
                <p>
                  <strong>@{invite.sender?.username ?? "unknown"}</strong>
                  {invite.sender?.name ? ` (${invite.sender.name})` : ""} is sharing
                  {invite.file ? ` ${invite.file.name}` : " a file"} with you.
                </p>
                <div className="file-actions">
                  {invite.status === "pending" ? (
                    <>
                      <button type="button" onClick={() => acceptInvite(invite.id)}>
                        Accept
                      </button>
                      <button type="button" className="btn-danger" onClick={() => declineInvite(invite.id)}>
                        Decline
                      </button>
                    </>
                  ) : (
                    <span className="mime-badge">{invite.status}</span>
                  )}
                </div>
                {invite.status === "accepted" && invite.sender?.email && (
                  <p className="success-text">Sender email: {invite.sender.email}</p>
                )}
              </div>
            ))}
          </div>
        )}
        {acceptedDownload && (
          <div className="invite-card" style={{ marginTop: 10 }}>
            <p className="success-text">
              Invite accepted. Download is ready. Sender email is now visible: {acceptedDownload.senderEmail ?? "not provided"}
            </p>
            <a href={acceptedDownload.url} target="_blank" rel="noreferrer" className="share-link">
              Download shared file ↗
            </a>
          </div>
        )}
      </div>

      <div className="page-header">
        <h1>Files</h1>
        <div className="upload-row">
          <input ref={fileInput} type="file" onChange={handleUpload} style={{ display: "none" }} />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload file"}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="loading">Loading…</div>
      ) : files.length === 0 ? (
        <div className="empty">No files. Upload one or use File.upload() on your desktop.</div>
      ) : (
        <table className="file-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Surface</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td>{f.name}</td>
                <td>
                  <span className="mime-badge">{f.mime}</span>
                </td>
                <td>{fmtBytes(f.size)}</td>
                <td>{f.surface}</td>
                <td className="file-actions file-actions-row">
                  <button type="button" onClick={() => toggleActions(f.id)}>
                    {openActionsFor === f.id ? "Close" : "Actions"}
                  </button>
                  {openActionsFor === f.id && (
                    <div className="file-actions-dropdown">
                      <div className="file-actions-tabs">
                        <button
                          type="button"
                          className={actionSection[f.id] === "share" ? "is-active" : ""}
                          onClick={() => setSection(f.id, "share")}
                        >
                          Share
                        </button>
                        <button
                          type="button"
                          className={actionSection[f.id] === "invite" ? "is-active" : ""}
                          onClick={() => setSection(f.id, "invite")}
                        >
                          Invite
                        </button>
                        <button
                          type="button"
                          className={actionSection[f.id] === "delete" ? "is-active" : ""}
                          onClick={() => setSection(f.id, "delete")}
                        >
                          Delete
                        </button>
                      </div>

                      {actionSection[f.id] === "share" && (
                        <div className="file-actions-panel">
                          <button
                            type="button"
                            onClick={() => copyShareLink(f.id)}
                            disabled={Boolean(shareCopyBusy[f.id])}
                          >
                            {shareCopyBusy[f.id] ? "Copying..." : "Copy 1-hour URL"}
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadFile(f)}
                            disabled={Boolean(shareOpenBusy[f.id])}
                          >
                            {shareOpenBusy[f.id] ? "Downloading..." : "Download"}
                          </button>
                          <span className="share-link">Copy URL or download directly.</span>
                        </div>
                      )}

                      {actionSection[f.id] === "invite" && (
                        <div className="file-actions-panel">
                          <div className="file-actions-input-row">
                            <input
                              type="text"
                              placeholder="recipient username"
                              value={inviteUsernames[f.id] ?? ""}
                              onChange={(e) =>
                                setInviteUsernames((prev) => ({ ...prev, [f.id]: e.target.value }))
                              }
                            />
                            <button type="button" onClick={() => invite(f.id)} disabled={inviteBusy[f.id]}>
                              {inviteBusy[f.id] ? "Sending..." : "Add invitation"}
                            </button>
                          </div>

                          <div className="invite-list file-actions-invites">
                            {outgoingForFile(f.id).length === 0 ? (
                              <span className="share-link">No invitations yet</span>
                            ) : (
                              outgoingForFile(f.id).map((invite) => (
                                <div className="invite-card" key={invite.id} style={{ margin: 0 }}>
                                  <p>
                                    @{invite.recipient?.username ?? "unknown"}
                                    {invite.recipient?.name ? ` (${invite.recipient.name})` : ""}
                                    {` — ${invite.status}`}
                                  </p>
                                  {invite.status !== "revoked" && (
                                    <button
                                      type="button"
                                      className="btn-danger"
                                      onClick={() => revokeInvite(invite.id, f.id)}
                                      disabled={Boolean(revokeBusy[invite.id])}
                                    >
                                      {revokeBusy[invite.id] ? "Removing..." : "Uninvite"}
                                    </button>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}

                      {actionSection[f.id] === "delete" && (
                        <div className="file-actions-panel">
                          <p>Delete this file permanently?</p>
                          <button type="button" className="btn-danger" onClick={() => del(f.id)}>
                            Confirm delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {inviteStatus[f.id] && <span className="share-link">{inviteStatus[f.id]}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section className="settings-section" style={{ marginTop: 16 }}>
        <h2>Outgoing Invitations</h2>
        {outgoingInvites.length === 0 ? (
          <p className="empty">No outgoing invites.</p>
        ) : (
          <div className="invite-list">
            {outgoingInvites.slice(0, 20).map((invite) => (
              <div className="invite-card" key={invite.id}>
                <p>
                  Shared {invite.file?.name ?? "file"} with @{invite.recipient?.username ?? "unknown"}
                  {invite.recipient?.name ? ` (${invite.recipient.name})` : ""} — {invite.status}
                </p>
                {invite.status === "accepted" && invite.recipient?.email && (
                  <p className="success-text">Recipient email: {invite.recipient.email}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
